// apps/web/src/components/FaceMeshWebcam.tsx

import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import * as faceapi from '@vladmandic/face-api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, CheckCircle, Camera as CameraIcon } from 'lucide-react';

const MODEL_URL = '/models';

interface FaceMeshWebcamProps {
  onCapture: (file: File) => void;
  capturedImage: string | null;
  onRecapture: () => void;
}

export const FaceMeshWebcam: React.FC<FaceMeshWebcamProps> = ({
  onCapture,
  onRecapture,
  capturedImage
}) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [feedback, setFeedback] = useState('Initializing face detection...');
  const [status, setStatus] = useState<'LOADING' | 'READY' | 'DETECTING' | 'LIVENESS_CHECK' | 'CAPTURED'>('LOADING');
  const [showManualButton, setShowManualButton] = useState(false);
  const [livenessChecks, setLivenessChecks] = useState({
    blinkCount: 0,
    movementCount: 0,
  });

  const captureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const blinkStateRef = useRef<{ previousState: string }>({
    previousState: 'OPEN',
  });
  const lastHeadPositionRef = useRef<{ x: number; y: number } | null>(null);
  const faceDetectionFailCountRef = useRef(0);

  // Calculate eye aspect ratio for blink detection
  const calculateEyeAspectRatio = (eyeLandmarks: any[]) => {
    if (!eyeLandmarks || eyeLandmarks.length < 6) return 1;

    const vertical1 = Math.hypot(
      eyeLandmarks[1].x - eyeLandmarks[5].x,
      eyeLandmarks[1].y - eyeLandmarks[5].y
    );
    const vertical2 = Math.hypot(
      eyeLandmarks[2].x - eyeLandmarks[4].x,
      eyeLandmarks[2].y - eyeLandmarks[4].y
    );
    const horizontal = Math.hypot(
      eyeLandmarks[0].x - eyeLandmarks[3].x,
      eyeLandmarks[0].y - eyeLandmarks[3].y
    );

    return (vertical1 + vertical2) / (2.0 * horizontal);
  };

  // Robust head movement detection with adaptive thresholds
  const detectHeadMovement = useCallback((landmarks: any) => {
    const nose = landmarks.getNose();
    if (!nose || nose.length === 0) return false;

    const currentNose = nose[4]; // Center of nose
    if (!currentNose) return false;

    if (!lastHeadPositionRef.current) {
      lastHeadPositionRef.current = { x: currentNose.x, y: currentNose.y };
      return false;
    }

    const lastPos = lastHeadPositionRef.current;
    const horizontalDist = Math.abs(currentNose.x - lastPos.x);
    const verticalDist = Math.abs(currentNose.y - lastPos.y);
    const totalMovement = Math.hypot(horizontalDist, verticalDist);

    const movementDetected = horizontalDist > 20 || verticalDist > 15;
    if (movementDetected && totalMovement > 20) {
      setLivenessChecks(prev => ({ ...prev, movementCount: prev.movementCount + 1 }));
      setFeedback('Head movement detected! ✓');
      lastHeadPositionRef.current = { x: currentNose.x, y: currentNose.y };
      return true;
    }
    return false;
  }, []);

  const clearPendingCapture = useCallback(() => {
    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
      captureTimeoutRef.current = null;
    }
  }, []);

  const handleManualCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      fetch(imageSrc).then(r => r.blob()).then(blob => {
        onCapture(new File([blob], "profile-pic.jpg", { type: "image/jpeg" }));
        setStatus('CAPTURED');
        setFeedback('Photo captured!');
      });
    }
  }, [onCapture]);

  // Face detection loop
  const detectFace = useCallback(async () => {
    if (status !== 'READY' && status !== 'LIVENESS_CHECK') return;
    if (capturedImage || !webcamRef.current?.video || !canvasRef.current) return;

    const video = webcamRef.current.video;
    if (video.readyState !== 4) return;

    const canvas = canvasRef.current;
    const displaySize = { width: video.videoWidth, height: video.videoHeight };
    faceapi.matchDimensions(canvas, displaySize);

    try {
      const detections = await faceapi
        .detectAllFaces(video, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
        .withFaceLandmarks();

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (detections.length === 1) {
        const detection = detections[0];
        const landmarks = detection.landmarks;

        // Perform liveness checks (ONLY Head Movement now)
        detectHeadMovement(landmarks);

        // Simplified Success Condition: Just one solid head movement
        const hasEnoughMovement = livenessChecks.movementCount >= 1;

        if (hasEnoughMovement) {
          setStatus('LIVENESS_CHECK');
          setFeedback('Scan Complete! Capturing...');

          if (!captureTimeoutRef.current) {
            captureTimeoutRef.current = setTimeout(() => {
              handleManualCapture();
            }, 1000);
          }
        } else {
          setFeedback(`Move your head slightly to scan...`);
        }
      } else {
        setFeedback(detections.length > 1 ? 'Multiple faces detected' : 'No face detected');
        clearPendingCapture();
      }
    } catch (error) {
      faceDetectionFailCountRef.current++;
      console.warn('Face detection error:', error);
      if (faceDetectionFailCountRef.current > 30) {
        setFeedback('Detection timeout - use manual capture');
      }
    }
  }, [status, capturedImage, livenessChecks, handleManualCapture, clearPendingCapture, detectHeadMovement]);

  // Initialize face-api models with retry logic
  useEffect(() => {
    const loadModels = async (retries = 0) => {
      try {
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL), // Robust model
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        setStatus('READY');
        setFeedback('Position your face and blink to confirm liveness');
        setTimeout(() => setShowManualButton(true), 5000);
      } catch (err) {
        if (retries < 3) {
          console.warn(`Model load failed, retrying (${retries + 1}/3)`);
          setTimeout(() => loadModels(retries + 1), 1500);
        } else {
          console.error('Failed to load models after retries');
          toast.error('AI models failed to load. Use manual capture.');
          setFeedback('Detection unavailable - use manual capture');
          setShowManualButton(true);
        }
      }
    };
    loadModels();
  }, []);

  // Detection loop with optimized timing
  useEffect(() => {
    if (status === 'READY' || status === 'LIVENESS_CHECK') {
      const interval = setInterval(() => {
        detectFace();
      }, 300);
      return () => clearInterval(interval);
    }
  }, [status, detectFace]);

  return (
    <div className="relative w-full aspect-[3/4] md:aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border-4 border-gray-900 flex items-center justify-center group">
      {capturedImage ? (
        <img src={capturedImage} alt="Captured" className="w-full h-full object-contain bg-black" />
      ) : (
        <>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            mirrored={true}
            className="w-full h-full object-contain" // Fix: Prevent zooming/cropping
            videoConstraints={{ facingMode: "user" }}
          />
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full object-contain"
          />

          {/* Visual Guide Overlay (Apple FaceID Style) */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            {/* Dimmed background outside oval */}
            <div className="absolute inset-0 bg-black/40 mask-oval-cutout"></div>

            {/* The Guide Oval */}
            <div className={`
               w-[280px] h-[380px] md:w-[320px] md:h-[420px] 
               border-[3px] rounded-[50%] transition-all duration-300
               ${status === 'LIVENESS_CHECK' ? 'border-green-400 shadow-[0_0_50px_rgba(74,222,128,0.5)] scale-105' : 'border-white/30 border-dashed'}
             `}></div>

            {/* Scanning Line Animation */}
            {status === 'READY' && (
              <div className="w-[280px] h-1 bg-blue-500/80 absolute top-1/2 -translate-y-1/2 shadow-[0_0_20px_rgba(59,130,246,0.8)] animate-scan-vertical"></div>
            )}
          </div>
        </>
      )}

      <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-black/80 text-white text-sm px-6 py-2 rounded-full backdrop-blur-md z-20 shadow-lg border border-white/10 font-medium whitespace-nowrap">
        {status === 'LOADING' && <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />}
        {status === 'CAPTURED' && <CheckCircle className="w-4 h-4 text-green-400 inline-block mr-2" />}
        {feedback}
      </div>

      <div className="absolute bottom-8 z-20 flex gap-2">
        {capturedImage ? (
          <Button type="button" onClick={onRecapture} variant="secondary" className="rounded-full px-8 py-6 text-lg shadow-xl hover:scale-105 transition-transform">
            <RefreshCw className="mr-2 h-5 w-5" />Recapture
          </Button>
        ) : (
          showManualButton && (
            <Button
              type="button"
              onClick={handleManualCapture}
              variant="default" // Changed to default for better visibility
              className="rounded-full animate-fade-in bg-white text-black hover:bg-gray-200 px-8 py-6 text-lg shadow-xl"
            >
              <CameraIcon className="mr-2 h-5 w-5" /> Capture Manually
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default FaceMeshWebcam;
